import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box, Flex, Link, useDisclosure } from '@chakra-ui/react';

import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { TCluster, TProductCluster } from 'pages/ClusterManagement/types';
import { useGetProductClusterQuery } from 'core/API/clusterApi';
import { PRODUCT_CLUSTER_LIST_FIELDS } from 'pages/ClusterManagement/const';
import { isUpcEnabled } from 'shared';
import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';

const EMPTY_DATA: any = [];

const useProductClusterContainer = () => {
    const translate = useTranslation();

    const [actionModalType, setActionModalType] = useState<'add' | 'edit' | 'delete' | undefined>();
    const [currentProductCluster, setCurrentProductCluster] = useState<TProductCluster | null>(null);

    const { data: productClusterData, isError, isFetching } = useGetProductClusterQuery();
    const actionModalProps = useDisclosure();

    const handleEditModal = useCallback((productCluster: TProductCluster) => {
        setActionModalType('edit');
        setCurrentProductCluster(productCluster);
        actionModalProps.onOpen();
    }, []);

    useEffect(() => {
        !actionModalProps.isOpen && setCurrentProductCluster(null);
    }, [actionModalProps.isOpen]);

    const companyInfo = useGetCompanyInfoSelector();
    const isUpc = isUpcEnabled(companyInfo);

    const columns: ColumnDef<TProductCluster, any>[] = useMemo(() => {
        const filteredTableHeader: ColumnDef<TProductCluster, any>[] = PRODUCT_CLUSTER_LIST_FIELDS.map(
            (header: string): ColumnDef<TProductCluster, any> => {
                switch (header) {
                    case 'ean':
                        return {
                            header: () => (isUpc ? 'UPC' : 'EAN'),
                            accessorKey: header,
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                return (
                                    <Flex>
                                        <Link onClick={() => handleEditModal(original)} color="blue.500">
                                            {original.upc || original.ean}
                                        </Link>
                                    </Flex>
                                );
                            },
                        };
                    case 'cluster':
                        return {
                            header: () => translate(`shopCluster.column.${header}`) as string,
                            id: header,
                            accessorFn: (row) => row.clusters?.map((cluster: TCluster) => cluster.label).join(', '),
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                return (
                                    <Flex gap={2} maxW="200px" flexWrap="wrap">
                                        {original.clusters?.map((cluster: TCluster) => (
                                            <Flex>
                                                <Box
                                                    w="20px"
                                                    h="20px"
                                                    borderRadius="50%"
                                                    bgColor={cluster.color}
                                                    border={cluster.color === '#ffffff' ? '1px solid #E2E8F0' : 'none'}
                                                    mr="4px"
                                                    display="inline-block"
                                                />
                                                {cluster.label}
                                            </Flex>
                                        ))}
                                    </Flex>
                                );
                            },
                        };
                    default:
                        return {
                            header: () => translate(`cluster.column.${header}`) as string,
                            accessorKey: header,
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                return <Flex>{original[header as keyof TProductCluster] as string}</Flex>;
                            },
                        };
                }
            },
        );

        return filteredTableHeader;
    }, []);

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const table = useReactTable<TProductCluster>({
        data: productClusterData?.data || EMPTY_DATA,
        columns,
        defaultColumn,
        sortingFns: SORTING_FNS,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const props = useMemo(
        () => ({
            isLoading: isFetching,
            table,
            actionModalProps,
            actionModalType,
            currentProductCluster,
            setActionModalType,
        }),
        [isFetching, table, actionModalProps.isOpen, currentProductCluster, actionModalType],
    );

    return props;
};

export type TProductClusterViewer = ReturnType<typeof useProductClusterContainer>;
export default useProductClusterContainer;
